import { Link, Typography } from "@mui/material";
import {
  AccessKind,
  JiraProjectPermission,
  Task,
  Tool,
  User,
} from "../../../app/backendTypes";
import UserSpanLoader from "../users/UserSpanLoader";
import UserSpan from "../users/UserSpan";
import SupportRoutingDisplay from "../support/SupportRoutingDisplay";
import SupportServiceDisplay from "../support/SupportServiceDisplay";

function TaskDescription(props: { task: Task }) {
  const kind = props.task.kind;

  let description = <></>;
  let note = null;

  switch (kind.kind) {
    case "ToolAccess": {
      switch (kind.inner.kind) {
        case AccessKind.On: {
          description = (
            <BoardingDescription
              name={kind.inner.tool}
              target={kind.inner.target}
              onboard
            />
          );

          break;
        }

        case AccessKind.Off: {
          description = (
            <BoardingDescription
              name={kind.inner.tool}
              target={kind.inner.target}
              onboard={false}
            />
          );

          break;
        }

        default: {
          description = (
            <Typography>We have no idea what is going on here</Typography>
          );
        }
      }

      note = kind.inner.note;

      break;
    }

    case "CatalogAccess": {
      switch (kind.inner.kind) {
        case AccessKind.On: {
          description = (
            <BoardingDescription
              name={kind.inner.catalog_name}
              target={kind.inner.target}
              onboard
            />
          );

          break;
        }

        case AccessKind.Off: {
          description = (
            <BoardingDescription
              name={kind.inner.catalog_name}
              target={kind.inner.target}
              onboard={false}
            />
          );

          break;
        }

        default: {
          description = (
            <Typography>We have no idea what is going on here</Typography>
          );
        }
      }

      note = kind.inner.note;

      break;
    }

    case "JiraProjectAccess": {
      description = (
        <JiraProjectAccessDescription
          target={kind.inner.target}
          projectPermission={kind.inner.permission}
          projectName={kind.inner.project_name}
        />
      );

      note = kind.inner.note;

      break;
    }

    case "BlackDuckProjectGroupAccess": {
      description = (
        <Typography>
          <UserSpanLoader id={kind.inner.target} /> will be assigned to{" "}
          <Typography component="span" color="secondary">
            {kind.inner.project_group_name}
          </Typography>
        </Typography>
      );

      note = kind.inner.note;

      break;
    }

    case "PolarisGroupAccess": {
      description = (
        <Typography>
          <UserSpanLoader id={kind.inner.target} /> will be assigned to{" "}
          <Typography component="span" color="secondary">
            {kind.inner.group_name}
          </Typography>
        </Typography>
      );

      note = kind.inner.note;

      break;
    }

    case "TableauSiteRoleChange": {
      description = (
        <Typography>
          <UserSpanLoader id={kind.inner.target} /> will be have their role
          changed to{" "}
          <Typography component="span" color="secondary">
            {kind.inner.role}
          </Typography>
        </Typography>
      );

      note = kind.inner.note;

      break;
    }

    case "TableauProjectRoleChange": {
      description = (
        <Typography>
          <UserSpanLoader id={kind.inner.target} /> will be have their role
          changed to{" "}
          <Typography component="span" color="secondary">
            {kind.inner.role}
          </Typography>{" "}
          in{" "}
          <Typography component="span" color="secondary">
            {kind.inner.project_name}
          </Typography>
        </Typography>
      );

      note = kind.inner.note;

      break;
    }

    case "DatasetAccess": {
      description = (
        <Typography>
          <UserSpanLoader id={kind.inner.target} /> will be have the following
          permission changes in{" "}
          <Typography component="span" color="secondary" fontWeight="bold">
            {kind.inner.dataset_id}
          </Typography>
          <br />
          <br />
          {kind.inner.actions.grant_read && (
            <Typography component="span" fontWeight="bold" color="success.main">
              &bull; grant read
              <br />
            </Typography>
          )}
          {kind.inner.actions.grant_write && (
            <Typography component="span" fontWeight="bold" color="success.main">
              &bull; grant write
              <br />
            </Typography>
          )}
          {kind.inner.actions.revoke_read && (
            <Typography component="span" fontWeight="bold" color="error">
              &bull; revoke read
              <br />
            </Typography>
          )}
          {kind.inner.actions.revoke_write && (
            <Typography component="span" fontWeight="bold" color="error">
              &bull; revoke write
              <br />
            </Typography>
          )}
        </Typography>
      );

      note = kind.inner.note;

      break;
    }

    case "EmployeeOffboarding": {
      const context = [];

      const keys = Object.keys(kind.inner.context).sort();

      for (const key of keys) {
        const tool = key as Tool;

        const value = kind.inner.context[tool];

        const result = value ? (
          <Typography component="span" color="success.main">
            Yes
          </Typography>
        ) : (
          <Typography component="span" color="error">
            No
          </Typography>
        );

        context.push(
          <>
            <Typography
              component="span"
              color={value ? "text.secondary" : "inherit"}
            >
              {tool}:{" "}
            </Typography>
            {result}
          </>
        );
      }

      const catalog_context = [];

      const catalog_keys = Object.keys(kind.inner.catalog_context || {}).sort();

      for (const key of catalog_keys) {
        const catalog = key;

        const value = kind.inner.catalog_context?.[catalog];

        const result = value?.is_offboarded ? (
          <Typography component="span" color="success.main">
            Yes
          </Typography>
        ) : (
          <Typography component="span" color="error">
            No
          </Typography>
        );

        catalog_context.push(
          <>
            <Typography
              component="span"
              color={value ? "text.secondary" : "inherit"}
            >
              {value?.catalog_name}:{" "}
            </Typography>
            {result}
          </>
        );
      }

      const custom_context = [];

      // Slack is no longer part of offboarding; hide it on historical tasks too.
      const custom_keys = Object.keys(kind.inner.custom_context || {})
        .filter((key) => key !== "Slack")
        .sort();

      for (const key of custom_keys) {
        const custom = key;

        const value = kind.inner.custom_context?.[custom];

        const result = value ? (
          <Typography component="span" color="success.main">
            Yes
          </Typography>
        ) : (
          <Typography component="span" color="error">
            No
          </Typography>
        );

        custom_context.push(
          <>
            <Typography
              component="span"
              color={value ? "text.secondary" : "inherit"}
            >
              {custom}:{" "}
            </Typography>
            {result}
          </>
        );
      }

      description = (
        <>
          <Typography>
            <UserSpan user={{ employee: kind.inner.employee } as User} /> will
            be offboarded
          </Typography>
          <Typography>
            {context.map((item) => (
              <>
                <br />
                {item}
              </>
            ))}

            {catalog_context.map((item) => (
              <>
                <br />
                {item}
              </>
            ))}

            {custom_context.map((item) => (
              <>
                <br />
                {item}
              </>
            ))}
          </Typography>
        </>
      );

      break;
    }

    case "Support": {
      description = (
        <>
          <Typography>
            Support ticket on behalf of{" "}
            <UserSpanLoader id={kind.inner.target} />
          </Typography>

          <br />

          <SupportRoutingDisplay routing={kind.inner.details.routing} />

          <SupportServiceDisplay service={kind.inner.details.service} />

          <br />

          {kind.inner.ticket && (
            <Typography>
              <Typography component="span" color="text.secondary">
                Issue:{" "}
              </Typography>
              <Link
                target="_blank"
                href={`https://jira.visma.com/browse/${kind.inner.ticket.issueKey}`}
              >
                {kind.inner.ticket.issueKey}
              </Link>
            </Typography>
          )}

          {kind.inner.ticket_state && (
            <Typography>
              <Typography component="span" color="text.secondary">
                Issue Resolution:{" "}
              </Typography>
              {kind.inner.ticket_state.issueResolution}
            </Typography>
          )}
        </>
      );

      note = kind.inner.note;

      break;
    }

    case "LoadContracts": {
      description = (
        <Typography>Corporate contracts will be synchronized</Typography>
      );

      break;
    }

    case "RetryEmployeeOffboardings": {
      description = <Typography>Offboardings will be retried</Typography>;

      break;
    }

    case "SyncUsers": {
      description = (
        <Typography>
          Local users will be synchronized with VOM employees{" "}
        </Typography>
      );

      break;
    }

    case "CarolineIndex": {
      description = <Typography>Caroline index will be updated</Typography>;

      break;
    }

    default: {
      description = (
        <Typography>We have no idea what is going on here</Typography>
      );
    }
  }

  const displayedNote = note ? (
    <>
      <br />
      <Typography variant="body2">
        Attached note:{" "}
        <Typography component="span" fontStyle="italic" variant="body2">
          {note}
        </Typography>
      </Typography>
    </>
  ) : (
    <></>
  );

  return (
    <>
      {description}
      {displayedNote}
    </>
  );
}

export default TaskDescription;

function JiraProjectAccessDescription(props: {
  target: number;
  projectPermission: JiraProjectPermission;
  projectName: string;
}) {
  const { target, projectPermission, projectName } = props;

  return (
    <>
      <Typography>
        <UserSpanLoader id={target} /> will be granted{" "}
        {projectPermission === JiraProjectPermission.Read ? (
          <Typography component="span" color="success.main">
            read
          </Typography>
        ) : (
          <>
            <Typography component="span" color="success.main">
              read
            </Typography>{" "}
            and{" "}
            <Typography component="span" color="success.main">
              write
            </Typography>
          </>
        )}{" "}
        permissions in{" "}
        <Typography component="span" color="secondary">
          {projectName}
        </Typography>
      </Typography>
    </>
  );
}

function BoardingDescription(props: {
  name: Tool | string;
  target: number;
  onboard: boolean;
}) {
  const { name, target, onboard } = props;

  const action = onboard ? "onboarded" : "offboarded";
  const actionPreposition = onboard ? "in" : "from";
  const actionColor = onboard ? "success.main" : "error";

  return (
    <>
      <Typography>
        <UserSpanLoader id={target} /> will be{" "}
        <Typography component="span" color={actionColor}>
          {action}
        </Typography>{" "}
        {actionPreposition}{" "}
        <Typography component="span" color="secondary">
          {name}
        </Typography>
      </Typography>
    </>
  );
}
